import React, { useEffect, useState } from 'react';
import ModalShell from '../ui/ModalShell';
import { Activity, MapPin, User, Clock, Car, Droplet, Calendar, Wrench, Gauge, Route, Package } from 'lucide-react';
import { classifyVozidlo } from './VozidloDlazdice';
import ProgressBarRemaining from './ProgressBarRemaining';

// STK platnost typicky 2 roky = 730 dní (po 4-leté první u nového auta).
// Použito jako baseline pro výpočet % zbývajícího času.
const STK_BASELINE_DNI = 730;

const SECTION_COLOR = '#0CC0DF';

function RowSimple({ k, v, dashed, subtle, textColor }) {
    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between',
            borderBottom: `1px dashed ${dashed}`,
            paddingBottom: 4,
        }}>
            <span style={{ color: subtle }}>{k}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', color: textColor, fontWeight: 600 }}>{v}</span>
        </div>
    );
}

/**
 * Tachometr — hodnota + kalibrační tlačítko. Klik vyvolá prompt, kde uživatel
 * zadá aktuální stav km. Backend POST /vozidla/{id}/kalibrace-tachometru
 * spočítá offset proti Traccar odometeru a od té chvíle se km_stav
 * přepočítává automaticky pro každý GPS bod.
 */
function RowTachometr({ vozidlo, km, dashed, subtle, textColor, isDark }) {
    const [busy, setBusy] = useState(false);
    // Kalibrace v ModalShell, ne window.prompt — ten na mobilu vyskočí jako
    // systémový dialog mimo aplikaci a chybu hlásil přes alert.
    const [kalibrace, setKalibrace] = useState(null);   // null | { hodnota, chyba }

    const handleKalibrace = () => setKalibrace({ hodnota: String(vozidlo.km_stav ?? ''), chyba: null });

    const ulozKalibraci = async () => {
        const aktualniKm = parseInt(String(kalibrace?.hodnota ?? '').replace(/[^0-9]/g, ''), 10);
        if (!aktualniKm || aktualniKm <= 0) {
            setKalibrace((k) => ({ ...k, chyba: 'Zadej stav tachometru v kilometrech.' }));
            return;
        }
        setBusy(true);
        try {
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';
            const r = await fetch(`/api/flotila/vozidla/${vozidlo.id}/kalibrace-tachometru`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrfToken, 'Accept': 'application/json' },
                credentials: 'same-origin',
                body: JSON.stringify({ aktualni_km: aktualniKm }),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                setKalibrace((k) => ({ ...k, chyba: data?.error || 'Kalibrace selhala.' }));
                return;
            }
            // Brutální ale spolehlivý refresh — Inspector + Live data se zaktualizují.
            window.location.reload();
        } catch (e) {
            setKalibrace((k) => ({ ...k, chyba: `Chyba: ${e.message}` }));
        } finally {
            setBusy(false);
        }
    };

    const btnColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';

    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            borderBottom: `1px dashed ${dashed}`,
            paddingBottom: 4,
        }}>
            <span style={{ color: subtle }}>Tachometr</span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontVariantNumeric: 'tabular-nums', color: textColor, fontWeight: 600 }}>
                    {km.toLocaleString('cs-CZ')} km
                </span>
                <button
                    type="button"
                    onClick={handleKalibrace}
                    disabled={busy}
                    title={vozidlo.traccar_odometer_offset_m
                        ? 'Překalibrovat tachometr (zadat aktuální stav)'
                        : 'Tachometr není zkalibrovaný — zadej aktuální stav, aby se počítal automaticky'}
                    style={{
                        background: vozidlo.traccar_odometer_offset_m ? 'transparent' : 'rgba(255,138,0,0.15)',
                        border: `1px solid ${vozidlo.traccar_odometer_offset_m ? 'transparent' : 'rgba(255,138,0,0.40)'}`,
                        borderRadius: 5,
                        padding: '2px 6px',
                        cursor: busy ? 'wait' : 'pointer',
                        color: vozidlo.traccar_odometer_offset_m ? btnColor : '#FF8A00',
                        fontSize: 10, fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        textTransform: 'uppercase', letterSpacing: 0.4,
                    }}
                >
                    <Gauge size={11} />
                    {vozidlo.traccar_odometer_offset_m ? 'Upravit' : 'Kalibrovat'}
                </button>
            </span>
            {kalibrace && (
                <ModalShell onClose={() => setKalibrace(null)} isDark={isDark} ikona={Gauge} barva="#0CC0DF" nadpis={`Tachometr ${vozidlo.spz}`} sirka={400}
                    paticka={(
                        <button type="button" onClick={ulozKalibraci} disabled={busy} className="w-full rounded-xl font-semibold"
                            style={{ height: 44, background: '#0CC0DF', color: '#0A0612', border: 'none', cursor: busy ? 'wait' : 'pointer' }}>
                            {busy ? 'Ukládám…' : 'Uložit kalibraci'}
                        </button>
                    )}>
                    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <p style={{ margin: 0, fontSize: 13, color: subtle }}>
                            Aktuální stav tachometru auta. Uloží se jako kalibrace — od teď se kilometry počítají samy podle GPS. Stačí jednou.
                        </p>
                        <input type="text" inputMode="numeric" autoFocus value={kalibrace.hodnota}
                            onChange={(e) => setKalibrace({ hodnota: e.target.value, chyba: null })}
                            onKeyDown={(e) => { if (e.key === 'Enter') ulozKalibraci(); }}
                            style={{ height: 44, borderRadius: 10, padding: '0 12px', fontSize: 16, fontVariantNumeric: 'tabular-nums',
                                background: isDark ? 'rgba(0,0,0,0.35)' : 'var(--surface-raised)', color: textColor,
                                border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }} />
                        {kalibrace.chyba && <p style={{ margin: 0, fontSize: 12, color: '#EF4444' }}>{kalibrace.chyba}</p>}
                    </div>
                </ModalShell>
            )}
        </div>
    );
}

function StatusKarta({ icon: Icon, label, value, color, isDark }) {
    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const bg = isDark ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.025)';
    const border = isDark ? 'rgba(255,255,255,0.05)' : 'var(--border-default)';
    return (
        <div style={{
            background: bg,
            border: `1px solid ${border}`,
            borderRadius: 7,
            padding: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
        }}>
            <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: 10, color: labelColor,
                textTransform: 'uppercase', letterSpacing: 0.5,
            }}>
                <Icon size={12} /> {label}
            </div>
            <div style={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: 700, fontSize: 14,
                color: color || (isDark ? '#fff' : 'var(--text-primary)'),
            }}>
                {value}
            </div>
        </div>
    );
}

function SectionTitle({ icon: Icon, color, children, isDark }) {
    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    return (
        <div style={{
            fontSize: 10, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 0.6,
            color: labelColor, marginBottom: 8,
            display: 'flex', alignItems: 'center', gap: 6,
        }}>
            <Icon size={11} style={{ color: color || labelColor }} />
            {children}
        </div>
    );
}

const UDALOST_LABELS = {
    rychlost: 'Překročení rychlosti',
    brzdeni: 'Prudké brzdění',
    akcelerace: 'Prudká akcelerace',
    zataceni: 'Ostré zatáčení',
};

const UDALOST_COLORS = {
    rychlost: '#EF4444',
    brzdeni: '#FFC857',
    akcelerace: SECTION_COLOR,
    zataceni: '#975ACD',
};

export default function InspectorPrehled({ vozidlo, ridic, isDark, onAddTankovani, onAddServis }) {
    const [udalosti, setUdalosti] = useState([]);

    // Tick 1× za sekundu — rerendere komponentu, aby "Doba dnes" v aktuálně
    // probíhající jízdě tikala živě (counter narůstá per-sekund). Pokud žádná
    // jízda neprobíhá, je to jen no-op render.
    const [, setNow] = useState(0);
    useEffect(() => {
        if (!vozidlo?.zacatek_probihajici_at) return;
        const i = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(i);
    }, [vozidlo?.zacatek_probihajici_at]);

    useEffect(() => {
        if (!vozidlo?.id) return;
        fetch(`/api/flotila/gps/udalosti?vozidlo_id=${vozidlo.id}`)
            .then((r) => r.json())
            .then((data) => {
                const list = Array.isArray(data) ? data : data?.data || [];
                setUdalosti(list.slice(0, 5));
            })
            .catch(() => {});
    }, [vozidlo?.id]);

    if (!vozidlo) return null;

    const status = classifyVozidlo(vozidlo);
    const rychlost = status === 'driving' ? `${Math.round(vozidlo.rychlost_kmh ?? 0)} km/h` : '0 km/h';
    const palivo = vozidlo.palivo_pct ?? null;
    const driverName = ridic ? `${ridic.jmeno || ''} ${ridic.prijmeni || ''}`.trim() : 'Bez řidiče';

    // Doba dnes = součet trvání dokončených dnešních jízd + (now - začátek
    // probíhající jízdy, pokud nějaká je). Tikuje se 1× za sekundu, aby
    // counter narůstal živě, dokud auto jede.
    const dokoncenaSec = (vozidlo.doba_dnes_dokoncena_min ?? 0) * 60;
    const probihajiciSec = vozidlo.zacatek_probihajici_at
        ? Math.max(0, Math.floor((Date.now() - new Date(vozidlo.zacatek_probihajici_at).getTime()) / 1000))
        : 0;
    const totalSec = dokoncenaSec + probihajiciSec;
    const dobaDnes = totalSec > 0
        ? `${Math.floor(totalSec / 3600)}:${String(Math.floor((totalSec % 3600) / 60)).padStart(2, '0')}:${String(totalSec % 60).padStart(2, '0')}`
        : '—';

    const stkDni = vozidlo.stk_dni_do_expirace;

    const km = vozidlo.km_stav ?? 0;
    const kmDoServ = vozidlo.km_do_servisu;
    const intervalMax = vozidlo.km_servis_interval ?? 30000;

    const dashedBorder = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)';
    const labelColor = isDark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)';
    const subtle = isDark ? 'rgba(255,255,255,0.65)' : 'var(--text-secondary)';
    const textColor = isDark ? '#fff' : 'var(--text-primary)';
    const eventBg = isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.025)';

    // Akční tlačítka
    const actionStyle = {
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        padding: '8px 12px', borderRadius: 7,
        background: isDark ? 'rgba(12,192,223,0.10)' : 'rgba(12,192,223,0.08)',
        color: SECTION_COLOR,
        border: '1px solid rgba(12,192,223,0.30)',
        fontSize: 12, fontWeight: 600,
        cursor: 'pointer',
    };

    return (
        <>
            {/* Aktuální stav */}
            <div style={{ marginBottom: 18 }}>
                <SectionTitle icon={Activity} color="#EF4444" isDark={isDark}>Aktuální stav</SectionTitle>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <StatusKarta icon={Activity} label="Rychlost" value={rychlost} isDark={isDark} />
                    <StatusKarta icon={MapPin} label="Pozice"
                        value={vozidlo.lat ? `${Number(vozidlo.lat).toFixed(4)}, ${Number(vozidlo.lng).toFixed(4)}` : '—'}
                        isDark={isDark} />
                    <StatusKarta icon={User} label="Řidič" value={driverName} isDark={isDark} />
                    <StatusKarta icon={Clock} label="Doba dnes" value={dobaDnes} isDark={isDark} />
                    <StatusKarta icon={Route} label="Najeto dnes"
                        value={vozidlo.km_dnes ? `${Number(vozidlo.km_dnes).toFixed(1)} km` : '—'}
                        isDark={isDark} />
                    <StatusKarta icon={Package} label="Jízd dnes"
                        value={vozidlo.pocet_jizd_dnes ?? 0}
                        isDark={isDark} />
                </div>
            </div>

            {/* Vozidlo */}
            <div style={{ marginBottom: 18 }}>
                <SectionTitle icon={Car} isDark={isDark}>Vozidlo</SectionTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
                    <RowSimple k="VIN" v={vozidlo.vin || '—'} dashed={dashedBorder} subtle={subtle} textColor={textColor} />
                    <RowTachometr vozidlo={vozidlo} km={km} dashed={dashedBorder} subtle={subtle} textColor={textColor} isDark={isDark} />
                    <RowSimple k="Spotřeba (Ø)" v={vozidlo.prumerna_spotreba ? `${vozidlo.prumerna_spotreba} l/100 km` : '—'} dashed={dashedBorder} subtle={subtle} textColor={textColor} />
                    <RowSimple k="Typ paliva" v={vozidlo.typ_paliva || '—'} dashed={dashedBorder} subtle={subtle} textColor={textColor} />
                </div>
            </div>

            {/* Palivo & opotřebení */}
            <div style={{ marginBottom: 18 }}>
                <SectionTitle icon={Droplet} color="#FF8A00" isDark={isDark}>Palivo & opotřebení</SectionTitle>
                {palivo !== null && (
                    <ProgressBarRemaining
                        label="Palivo v nádrži"
                        remaining={palivo} total={100}
                        color="linear-gradient(90deg, #FF8A00, #FFB347)"
                        isDark={isDark}
                    />
                )}
                {stkDni !== null && stkDni !== undefined && (
                    <ProgressBarRemaining
                        label={`STK platnost (${stkDni} ${Math.abs(stkDni) === 1 ? 'den' : 'dní'})`}
                        remaining={stkDni} total={STK_BASELINE_DNI}
                        isDark={isDark}
                    />
                )}
                {kmDoServ !== null && kmDoServ !== undefined && (
                    <ProgressBarRemaining
                        label={`Servisní interval (${kmDoServ.toLocaleString('cs-CZ')} km)`}
                        remaining={kmDoServ} total={intervalMax}
                        isDark={isDark}
                    />
                )}
            </div>

            {/* Poslední události */}
            {udalosti.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                    <SectionTitle icon={Calendar} isDark={isDark}>Poslední události</SectionTitle>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {udalosti.map((u) => (
                            <div key={u.id} style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                fontSize: 12, padding: '6px 10px',
                                background: eventBg, borderRadius: 6,
                            }}>
                                <span style={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    background: UDALOST_COLORS[u.typ] || subtle,
                                }} />
                                <span style={{ color: textColor }}>{UDALOST_LABELS[u.typ] || u.typ}</span>
                                <span style={{
                                    marginLeft: 'auto', color: labelColor,
                                    fontVariantNumeric: 'tabular-nums',
                                }}>
                                    {u.zaznameno_at ? new Date(u.zaznameno_at).toLocaleString('cs-CZ', {
                                        hour: '2-digit', minute: '2-digit',
                                        day: 'numeric', month: 'numeric',
                                    }) : '—'}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Akční tlačítka — jen kde je kdo obslouží (v Autech ano, na Živě ne) */}
            {(onAddTankovani || onAddServis) && <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <button type="button" style={actionStyle} onClick={() => onAddTankovani?.(vozidlo)}>
                    <Droplet size={13} /> Tankování
                </button>
                <button type="button" style={actionStyle} onClick={() => onAddServis?.(vozidlo)}>
                    <Wrench size={13} /> Servis
                </button>
            </div>}
        </>
    );
}
